import { formatCompactCurrency, formatCount, formatPercent } from '../../../lib/format.js';
import { DEFAULT_KPI_META, MEDIA_KPIS } from './constants';
import type { LeaderboardRow } from './marketLeaderboardData';
import type { MarketRow } from './marketPerformanceData';
import type { MarketCoverage, MediaKpi } from './types';

const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

export function marketName(code: string): string {
  if (!/^[a-z]{2}$/i.test(code)) return code;
  return REGION_NAMES.of(code.toUpperCase()) ?? code;
}

export function kpiById(id: string): MediaKpi {
  return MEDIA_KPIS.find((kpi) => kpi.id === id) ?? DEFAULT_KPI_META;
}

export function toScopeFilters(
  countries: string[],
  creativeTypes: string[],
  objectives: string[],
): Record<string, string[]> | undefined {
  const out = {
    ...(countries.length ? { country: countries } : {}),
    ...(creativeTypes.length ? { creativeType: creativeTypes } : {}),
    ...(objectives.length ? { campaignObjective: objectives } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

// The curated KPIs the scope's metrics dict offers, labelled as the dict labels them. Empty stays
// empty: falling back to the default would re-offer exactly what the narrowing removed.
export function availableKpis(
  metrics: Readonly<Record<string, { label: string }>> | undefined,
): MediaKpi[] {
  if (!metrics) return MEDIA_KPIS;
  return MEDIA_KPIS.flatMap((kpi) => {
    const metric = metrics[kpi.id];
    return metric === undefined ? [] : [{ ...kpi, label: metric.label }];
  });
}

export function formatKpiValue(value: number | null, kpi: MediaKpi, symbol: string): string {
  if (value === null) return '-';
  if (kpi.unit === 'percent') return formatPercent(value);
  if (kpi.unit === 'currency') return `${symbol}${value.toFixed(1)}`;
  return formatCount(value);
}

export function formatMarketMillions(valueMillions: number, symbol = '€'): string {
  return formatCompactCurrency(valueMillions * 1_000_000, symbol);
}

export function formatMarketThousands(valueThousands: number, symbol = '€'): string {
  return formatCompactCurrency(valueThousands * 1_000, symbol);
}

export function formatMarketUplift(valueThousands: number | null, symbol = '€'): string {
  return formatCompactCurrency(
    valueThousands === null ? null : valueThousands * 1_000,
    symbol,
    true,
  );
}

export function formatMarketRate(value: number): string {
  return formatPercent(value / 100);
}

export function formatMarketWow(value: number): string {
  return `${value >= 0 ? '+' : ''}${formatMarketRate(value)}`;
}

export function resolveMarket(selectedCountry: string, markets: MarketRow[]): MarketRow | null {
  if (markets.length === 0) return null;
  const normalized = selectedCountry.trim().toLowerCase();
  const direct = markets.find(
    (market) =>
      market.code.toLowerCase() === normalized || market.market.toLowerCase() === normalized,
  );
  return direct ?? null;
}

export function filterByCountries<Row extends { code: string; market: string }>(
  rows: Row[],
  countries: string[],
): Row[] {
  if (countries.length === 0) return rows;
  return rows.filter((row) =>
    countries.some((country) => {
      const normalized = country.trim().toLowerCase();
      return row.code.toLowerCase() === normalized || row.market.toLowerCase() === normalized;
    }),
  );
}

export function barFillPercent(performanceIndex: number, maxPerformanceIndex: number): number {
  return Math.max(5, (performanceIndex / maxPerformanceIndex) * 100);
}

export function maxPerformanceIndex(rows: LeaderboardRow[]): number {
  return Math.max(1, ...rows.map((row) => row.performanceIndex));
}

export function coverageNote(coverage: MarketCoverage | null): string | null {
  if (!coverage || coverage.shown >= coverage.total) return null;
  return `Showing the top ${coverage.shown} of ${coverage.total} markets · ${Math.round(coverage.spendShare)}% of spend`;
}
